import { Component, OnInit, ViewChild } from "@angular/core";
import { MatSort, MatSortable, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { Router } from "@angular/router";
import { FormBuilder, FormGroup } from "@angular/forms";
import { IPreplanRescue } from "../interfaces/ert-preplan-rescue/ert-preplan-rescue";
import { PreplanRescueService } from "../services/preplanrescue/preplan-rescue.service";
import { ILoggedUserData } from "../interfaces/common/loggedUserData";
import { LoggedUserService } from "../services/common/logged-user.service";
import {
  FilterCriteria,
  FilterService,
} from "../services/common/filter.service";
import { RoleNames } from "../utils/role-names";

@Component({
  selector: "app-ert-preplan-rescue",
  templateUrl: "./ert-preplan-rescue.component.html",
  styles: [
    `
      .button-container {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
      }

      .search-form {
        margin-bottom: 20px;
      }

      .search-input {
        width: 100%;
      }
    `,
  ],
})
export class ErtPreplanRescueComponent implements OnInit {
  title = "Preplan Rescue";
  totalItems: number = 0;
  sortedBy: string;
  isLoading: boolean = false;
  searchForm: FormGroup;

  // Add pagination properties
  pageSize = 10;
  pageSizeOptions: number[] = [5, 10, 25, 50];
  paginationInfo = "";

  activeFilters: FilterCriteria[] = [];
  showFilterPanel = false;

  readonly preplan_rescue_filters_storage_key = "ert-preplan-rescue-filters";
  readonly preplan_rescue_saved_filters_storage_key =
    "ert-preplan-rescue-saved-filters";

  displayedColumns: Map<string, string> = new Map<string, string>([
    ["id", "ID"],
    ["dateTimeAdd", "Date"],
    ["areaHO", "Area HO"],
    ["areaSTL", "Area STL"],
    ["reasonOfEntry", "Reason for Entry"],
    ["durationOfWork", "Duration of Work"],
    ["maintsRepOriginator", "Maints Rep/Originator"],
    ["actions", "Actions"],
  ]);

  dataSource: MatTableDataSource<IPreplanRescue> =
    new MatTableDataSource<IPreplanRescue>();

  isDeletedEnabled: boolean = false;

  displayedColumnsArray: string[] = Array.from(this.displayedColumns.keys());
  currentUser: ILoggedUserData;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  constructor(
    private router: Router,
    private service: PreplanRescueService,
    private formBuilder: FormBuilder,
    private loggedUserService: LoggedUserService,
    private filterService: FilterService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.initSearchForm();

    this.loggedUserService.getLoggedUserData().subscribe((user) => {
      this.currentUser = user;
      this.isDeletedEnabled =
        user.role === RoleNames.ERT_ADMINISTRATOR ? true : false;
      // Initialize filter service
      this.filterService.loadActiveFiltersFromStorage(
        this.preplan_rescue_filters_storage_key
      );
      this.filterService.loadSavedFiltersFromStorage(
        this.preplan_rescue_saved_filters_storage_key
      );

      // Load data from service
      this.loadData();
    });
  }

  initSearchForm(): void {
    this.searchForm = this.formBuilder.group({
      searchTerm: [""],
    });

    // Subscribe to value changes to implement real-time filtering
    this.searchForm.get("searchTerm")?.valueChanges.subscribe((value) => {
      this.applyFilter(value);
    });
  }

  applyFilter(filterValue: string): void {
    // Create custom filter predicate that combines search term with advanced filters
    this.dataSource.filterPredicate = (
      data: IPreplanRescue,
      filterString: string
    ) => {
      // Parse the filter string - format is "searchTerm||activeFilters"
      const parts = filterString.split("||");
      const searchTerm = parts[0].toLowerCase();
      let activeFilters: FilterCriteria[] = [];

      try {
        if (parts[1]) {
          activeFilters = JSON.parse(parts[1]);
        }
      } catch (error) {
        console.error("Error parsing active filters:", error);
      }

      // Basic search
      const searchMatch = searchTerm
        ? String(data.id || "")
            .toLowerCase()
            .includes(searchTerm) ||
          (data.areaHO || "").toLowerCase().includes(searchTerm) ||
          (data.areaSTL || "").toLowerCase().includes(searchTerm) ||
          (data.reasonOfEntry || "").toLowerCase().includes(searchTerm) ||
          (data.durationOfWork || "").toLowerCase().includes(searchTerm) ||
          (data.maintsRepOriginator || "").toLowerCase().includes(searchTerm)
        : true; // If no search term, consider it a match

      // Advanced filters match
      let advancedFiltersMatch = true;
      if (activeFilters.length > 0) {
        advancedFiltersMatch = activeFilters.every((filter) => {
          const value = this.getPropertyValue(data, filter.column);

          if (value === null || value === undefined) {
            return false;
          }

          switch (filter.operator) {
            case "contains":
              return String(value)
                .toLowerCase()
                .includes(String(filter.value).toLowerCase());
            case "equals":
              return (
                String(value).toLowerCase() ===
                String(filter.value).toLowerCase()
              );
            case "startsWith":
              return String(value)
                .toLowerCase()
                .startsWith(String(filter.value).toLowerCase());
            case "endsWith":
              return String(value)
                .toLowerCase()
                .endsWith(String(filter.value).toLowerCase());
            case "greaterThan":
              return Number(value) > Number(filter.value);
            case "lessThan":
              return Number(value) < Number(filter.value);
            default:
              return true;
          }
        });
      }

      return searchMatch && advancedFiltersMatch;
    };

    // Combine search term and active filters
    const combinedFilter =
      filterValue.trim().toLowerCase() +
      "||" +
      JSON.stringify(this.activeFilters);
    this.dataSource.filter = combinedFilter;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    this.totalItems = this.dataSource.filteredData.length;
    this.updatePaginationInfo();
  }

  // Helper to get property value for filtering
  getPropertyValue(data: IPreplanRescue, property: string): any {
    switch (property) {
      case "id":
        return data.id;
      case "dateTimeAdd":
        return data.dateTimeAdd;
      case "areaHO":
        return data.areaHO;
      case "areaSTL":
        return data.areaSTL;
      case "reasonOfEntry":
        return data.reasonOfEntry;
      case "durationOfWork":
        return data.durationOfWork;
      case "maintsRepOriginator":
        return data.maintsRepOriginator;
      default:
        return "";
    }
  }

  // Toggle filter panel
  toggleFilterPanel() {
    this.showFilterPanel = !this.showFilterPanel;
  }

  // Handler for the filtersChanged event from advanced-filter-panel component
  onFiltersChanged(filters: FilterCriteria[]): void {
    // Filter service already updates the activeFilters array,
    // we just need to trigger a filter
    this.applyFilter(this.searchForm.get("searchTerm")?.value || "");
  }

  // Clear all filters
  clearAllFilters() {
    this.filterService.clearAllFilters(this.preplan_rescue_filters_storage_key);
    this.searchForm.patchValue({ searchTerm: "" });
    this.applyFilter("");
  }

  // Get column display name for UI
  getColumnDisplayName(columnKey: string): string {
    return this.displayedColumns.get(columnKey) || columnKey;
  }

  clearSearch(): void {
    this.searchForm.get("searchTerm")?.setValue("");
  }

  loadData(): void {
    const facilityId = Number(this.currentUser?.facilityId) || 1;
    this.service.getPreplanRescue(facilityId).subscribe((data) => {
      this.dataSource.data = data;

      // Configure the filter predicate to search through all displayable columns
      this.dataSource.filterPredicate = (
        data: IPreplanRescue,
        filter: string
      ) => {
        const searchTerms = filter.toLowerCase();
        return Object.keys(data).some((key) => {
          if (this.displayedColumns.has(key) && data[key]) {
            return data[key].toString().toLowerCase().includes(searchTerms);
          }
          return false;
        });
      };

      this.totalItems = this.dataSource.data.length;

      setTimeout(() => {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.sort.sort({ id: "dateTimeAdd", start: "desc" } as MatSortable);
        this.sort.sortChange.subscribe((sortState: Sort) => {
          this.onSortChange(sortState);
        });
        this.sortedBy = `Sorted by ${this.displayedColumns.get("dateTimeAdd")}`;
        this.updatePaginationInfo();
      }, 10);

      // Subscribe to active filters changes
      this.filterService
        .getActiveFilters(this.preplan_rescue_filters_storage_key)
        .subscribe((filters) => {
          this.activeFilters = filters;
          if (this.dataSource) {
            this.applyFilter(this.searchForm.get("searchTerm")?.value || "");
          }
        });

      this.isLoading = false;
    });
  }

  // Add pagination info update method
  updatePaginationInfo(): void {
    if (this.paginator) {
      const startIndex = this.paginator.pageIndex * this.paginator.pageSize + 1;
      const endIndex = Math.min(
        (this.paginator.pageIndex + 1) * this.paginator.pageSize,
        this.totalItems
      );
      this.paginationInfo = `Showing ${startIndex} to ${endIndex} of ${this.totalItems}`;
    } else {
      this.paginationInfo = "";
    }
  }

  // Page change handler
  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.updatePaginationInfo();
  }

  onSortChange(sortState: Sort) {
    if (sortState.direction.length > 0) {
      this.sortedBy = `Sorted by ${this.displayedColumns.get(
        sortState.active
      )}`;
    } else {
      this.sortedBy = "Not sorted";
    }
  }

  navigateToNewEntry() {
    this.router.navigate(["/preplan-rescue/create"]);
  }

  editPreplanRescue(id: number) {
    this.router.navigate(["/preplan-rescue/edit", id]);
  }

  confirmDeletePreplan(id: number) {
    if (confirm(`Are you sure you want to delete preplan rescue ${id}?`)) {
      this.isLoading = true;
      this.service
        .deletePreplanRescue(Number(this.currentUser?.facilityId), id)
        .subscribe(() => {
          this.loadData();
        });
    }
  }
}
